/* eslint-disable no-empty */
/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  getPagedData,
  setEndpoint,
  setFixedFilters,
  setPagination,
  setQueryParams
} from '@store/DynamicSlice/dynamicTableSlice';
import { getTaxManegerByTaxTypeList } from '@store/Finance/TaxManagers/TaxManagerSlice';
import { GetPriceListList } from '@store/INV/PriceList/PriceListSlice';
import {
  FIELD_OPTIONS,
  LS_KEY,
  presetA4_63x38,
  presetRoll80x40,
  type FieldKey,
  type LabelLayoutConfig
} from './labelLayout';
import Decimal from 'decimal.js';

type AnyItem = any;

export const TABLE_KEY = 'PriceList_BarcodePrint_ItemsIn';

export const NULL_FIXED = {
  itemCode: null,
  name: null,
  categoryId: null,
  unitOfMeasure: null,
  brandId: null,
  barcode: null,
  itemType: null
};

export const FILTER_OPTIONS = [
  { value: 'barcode', label: 'الباركود', icon: 'bi-upc-scan' },
  { value: 'name', label: 'الاسم', icon: 'bi-fonts' },
  { value: 'itemCode', label: 'كود الصنف', icon: 'bi-tag' }
] as const;

export type FilterKey = (typeof FILTER_OPTIONS)[number]['value'];
export type PaperMode = 'ROLL' | 'A4';
export type PriceMode = 'EXCL' | 'INCL';

export const ALL_BARCODES = '__ALL__';

export const getRowKey = (item: AnyItem) => `${item?.itemId}-${item?.unitOfMeasure}`;

export const splitBarcodes = (barcodeText: string | null | undefined) => {
  const raw = String(barcodeText ?? '').trim();
  if (!raw) return [];
  return raw
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
};

const round2 = (n: number) => Math.round(n * 100) / 100;

export type LabelRow = { key: string; barcode: string; item: AnyItem; price: number };

export function usePriceListBarcodePrint() {
  const { i18n } = useTranslation();
  const dispatch = useAppDispatch();

  // Price Lists
  const { PriceList, loading: priceListLoading, error: priceListError } = useAppSelector(state => state.PriceList);
  const priceLists = PriceList ?? [];
  const [selectedPriceListId, setSelectedPriceListId] = useState<string>('');

  useEffect(() => {
    if ((priceLists?.length ?? 0) === 0) dispatch(GetPriceListList());
  }, []);

  // Dynamic table
  const table = useAppSelector(state => state.dynamicTable[TABLE_KEY]);
  const loading = table?.loading ?? false;
  const pageSize = table?.pageSize ?? 10;
  const data = (table?.data ?? []) as AnyItem[];

  useEffect(() => {
    // dispatch(setEndpoint({ tableKey: TABLE_KEY, endpoint: 'Inv/GetPriceListItemsInPriceListId_Paged' }));
    dispatch(setEndpoint({ tableKey: TABLE_KEY, endpoint: 'Inv/GetItemsWithUomLookup' }));
    dispatch(setPagination({ tableKey: TABLE_KEY, pageIndex: 1, pageSize: 10 }));
    dispatch(setFixedFilters({ tableKey: TABLE_KEY, fixedFilters: { ...NULL_FIXED } }));
    dispatch(getTaxManegerByTaxTypeList('Sales'));
  }, []);

  // Selection + qty + barcode pick
  const [selectedMap, setSelectedMap] = useState<Record<string, AnyItem>>({});
  const [qtyMap, setQtyMap] = useState<Record<string, number>>({});
  const [barcodePickMap, setBarcodePickMap] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<'items' | 'selected'>('items');

  const selectedItems = useMemo(() => Object.values(selectedMap), [selectedMap]);

  const toggleSelect = useCallback(
    (item: AnyItem) => {
      const k = getRowKey(item);
      const exists = !!selectedMap[k];

      setSelectedMap(prev => {
        const next = { ...prev };
        if (exists) delete next[k];
        else next[k] = item;
        return next;
      });

      setQtyMap(prev => {
        const next = { ...prev };
        if (exists) delete next[k];
        else if (next[k] == null) next[k] = 1;
        return next;
      });

      setBarcodePickMap(prev => {
        const next = { ...prev };
        if (exists) {
          delete next[k];
          return next;
        }
        const barcodes = splitBarcodes(item?.barcode);
        next[k] = barcodes[0] ?? '';
        return next;
      });
    },
    [selectedMap]
  );

  const removeSelected = useCallback((k: string) => {
    setSelectedMap(prev => {
      const next = { ...prev };
      delete next[k];
      return next;
    });
    setQtyMap(prev => {
      const next = { ...prev };
      delete next[k];
      return next;
    });
    setBarcodePickMap(prev => {
      const next = { ...prev };
      delete next[k];
      return next;
    });
  }, []);

  useEffect(() => {
    if (!selectedPriceListId) return;

    setSelectedMap({});
    setQtyMap({});
    setBarcodePickMap({});
    setActiveTab('items');

    const queryParams: any = {
      PriceListId: selectedPriceListId,
      Scope: 1
      // WithLastPrice: true,
    };

    dispatch(setQueryParams({ tableKey: TABLE_KEY, queryParams }));
    // dispatch(setQueryParams({ tableKey: TABLE_KEY, queryParams: { PriceListId: selectedPriceListId } }));
    dispatch(setPagination({ tableKey: TABLE_KEY, pageIndex: 1, pageSize: 10 }));
    dispatch(setFixedFilters({ tableKey: TABLE_KEY, fixedFilters: { ...NULL_FIXED } }));
    dispatch(getPagedData({ tableKey: TABLE_KEY }));
  }, [selectedPriceListId]);

  // Search
  const [filterKey, setFilterKey] = useState<FilterKey>('barcode');
  const [filterText, setFilterText] = useState('');

  const applySearch = useCallback(() => {
    const text = filterText.trim();
    dispatch(
      setFixedFilters({
        tableKey: TABLE_KEY,
        fixedFilters: { ...NULL_FIXED, [filterKey]: text ? text : null }
      })
    );
    dispatch(setPagination({ tableKey: TABLE_KEY, pageIndex: 1, pageSize }));
    dispatch(getPagedData({ tableKey: TABLE_KEY }));
  }, [dispatch, filterKey, filterText, pageSize]);

  const clearSearch = useCallback(() => {
    setFilterText('');
    setFilterKey('barcode');
    dispatch(setFixedFilters({ tableKey: TABLE_KEY, fixedFilters: { ...NULL_FIXED } }));
    dispatch(setPagination({ tableKey: TABLE_KEY, pageIndex: 1, pageSize }));
    dispatch(getPagedData({ tableKey: TABLE_KEY }));
  }, [dispatch, pageSize]);

  // Print options
  const [showName, setShowName] = useState(true);
  const [showCode, setShowCode] = useState(false);
  const [showUom, setShowUom] = useState(false);
  const [showPrice, setShowPrice] = useState(true);
  const [showBarcodeText, setShowBarcodeText] = useState(true);

  const [printAllBarcodes, setPrintAllBarcodes] = useState(false);
  const [paperMode, setPaperMode] = useState<PaperMode>('ROLL');
  const [priceMode, setPriceMode] = useState<PriceMode>('EXCL');
  const [currency, setCurrency] = useState('');

  const [usePriceFactor, setUsePriceFactor] = useState(false);
  const [priceFactor, setPriceFactor] = useState<number>(1);

  const safePriceFactor = useMemo(() => {
    const v = Number(priceFactor);
    return Number.isFinite(v) && v > 0 ? v : 1;
  }, [priceFactor]);

  // Layout
  const [layout, setLayout] = useState<LabelLayoutConfig>(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) return JSON.parse(raw);
    } catch {}
    return presetRoll80x40();
  });

  const applyPreset = useCallback((mode: PaperMode) => {
    setPaperMode(mode);
    setLayout(mode === 'ROLL' ? presetRoll80x40() : presetA4_63x38());
  }, []);

  const setLayoutField = useCallback(<K extends FieldKey>(key: K, patch: Partial<LabelLayoutConfig['fields'][K]>) => {
    setLayout(prev => ({
      ...prev,
      fields: {
        ...prev.fields,
        [key]: { ...(prev.fields[key] as any), ...(patch as any) }
      }
    }));
  }, []);

  const [selectedFieldKey, setSelectedFieldKey] = useState<FieldKey>('name');
  const selectedField = useMemo(
    () => FIELD_OPTIONS.find(x => x.key === selectedFieldKey) ?? FIELD_OPTIONS[0],
    [selectedFieldKey]
  );

  // VAT map
  const taxList = useAppSelector(state => state.TaxManager?.taxTypeList);
  const taxRateMap = useMemo(() => {
    const m: Record<string, number> = {};
    (taxList ?? []).forEach((x: any) => {
      const cat = String(x?.taxCategory ?? '').toUpperCase();
      const rate = Number(x?.taxRate);
      if (cat) m[cat] = Number.isFinite(rate) ? rate : 0;
    });
    return m;
  }, [taxList]);

  const defaultStandardVat = 15;

  const getItemTaxCategory = useCallback((item: AnyItem) => {
    const raw = item?.tax;
    return raw == null || String(raw).trim() === '' ? 'STANDARD' : String(raw).trim().toUpperCase();
  }, []);

  const getTaxRatePct = useCallback(
    (item: AnyItem) => {
      const cat = getItemTaxCategory(item);
      const found = taxRateMap[cat];
      if (Number.isFinite(found)) return found;
      if (cat === 'STANDARD') return taxRateMap['STANDARD'] ?? defaultStandardVat;
      return 0;
    },
    [getItemTaxCategory, taxRateMap]
  );

  // const toIncl = useCallback((excl: number, ratePct: number) => round2(excl * (1 + ratePct / 100)), []);
  // const toIncl = useCallback((excl: number, ratePct: number) => excl * (1 + ratePct / 100), []);

  const D = (v: Decimal.Value) => new Decimal(v ?? 0);

  const toIncl = useCallback((excl: number, ratePct: number) => {
    const factor = D(1).plus(D(ratePct).div(100));
    return D(excl).mul(factor).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber();
  }, []);

  const getDisplayName = useCallback(
    (item: AnyItem) => (i18n.language === 'en' ? item?.name : item?.name2) ?? item?.name2 ?? item?.name ?? '',
    [i18n.language]
  );

  const getDisplayUom = useCallback(
    (item: AnyItem) =>
      (i18n.language === 'en' ? item?.unitOfMeasureName : item?.unitOfMeasureName2) ??
      item?.unitOfMeasureName2 ??
      item?.unitOfMeasureName ??
      '',
    [i18n.language]
  );

  const getUnitPriceExcl = useCallback((item: AnyItem) => Number(item?.unitPrice ?? 0) || 0, []);

  const getUnitPriceForPrint = useCallback(
    (item: AnyItem) => {
      // debugger
      const excl = getUnitPriceExcl(item);
      let result = excl;

      if (priceMode !== 'EXCL') {
        const ratePct = getTaxRatePct(item);
        result = toIncl(excl, ratePct);
      }

      // if (usePriceFactor) result = round2(result * safePriceFactor);
      if (usePriceFactor) {
        result = D(result).mul(safePriceFactor).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber();
      }

      return result;
    },
    [getTaxRatePct, getUnitPriceExcl, priceMode, safePriceFactor, toIncl, usePriceFactor]
  );

  // Build labels
  const labels: LabelRow[] = useMemo(() => {
    const rows: LabelRow[] = [];

    for (const item of selectedItems) {
      const k = getRowKey(item);
      const qty = Math.max(1, Math.floor(Number(qtyMap[k] ?? 1)));

      const barcodes = splitBarcodes(item?.barcode);
      if (!barcodes.length) continue;

      const picked = barcodePickMap[k];
      let chosen: string[] = [];

      if (printAllBarcodes) chosen = barcodes;
      else if (picked === ALL_BARCODES) chosen = barcodes;
      else if (picked && barcodes.includes(picked)) chosen = [picked];
      else chosen = barcodes.slice(0, 1);

      const p = getUnitPriceForPrint(item);

      for (const b of chosen) {
        for (let i = 0; i < qty; i++) {
          rows.push({ key: `${k}|${b}|${i}`, barcode: b, item, price: p });
        }
      }
    }

    return rows;
  }, [selectedItems, qtyMap, barcodePickMap, printAllBarcodes, getUnitPriceForPrint]);

  const totalBarcodes = useMemo(() => {
    let count = 0;
    selectedItems.forEach(item => {
      const k = getRowKey(item);
      const qty = qtyMap[k] ?? 1;
      const barcodes = splitBarcodes(item?.barcode);

      if (printAllBarcodes) count += barcodes.length * qty;
      else {
        const picked = barcodePickMap[k];
        if (picked === ALL_BARCODES) count += barcodes.length * qty;
        else count += qty;
      }
    });
    return count;
  }, [selectedItems, qtyMap, barcodePickMap, printAllBarcodes]);

  // Layout persistence
  const saveLayout = useCallback(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(layout));
  }, [layout]);

  const loadLayout = useCallback(() => {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as LabelLayoutConfig;
  }, []);

  const getPLName = useCallback(
    (pl: any) => (i18n.language === 'en' ? pl?.name : pl?.name2) ?? pl?.name2 ?? pl?.name ?? '',
    [i18n.language]
  );

  return {
    language: i18n.language,

    priceLists,
    priceListLoading,
    priceListError,

    table,
    loading,
    pageSize,
    data,

    selectedMap,
    selectedItems,
    qtyMap,
    setQtyMap,
    barcodePickMap,
    setBarcodePickMap,
    activeTab,
    setActiveTab,
    toggleSelect,
    removeSelected,

    selectedPriceListId,
    setSelectedPriceListId,

    filterKey,
    setFilterKey,
    filterText,
    setFilterText,
    applySearch,
    clearSearch,

    showName,
    setShowName,
    showCode,
    setShowCode,
    showUom,
    setShowUom,
    showPrice,
    setShowPrice,
    showBarcodeText,
    setShowBarcodeText,

    printAllBarcodes,
    setPrintAllBarcodes,
    paperMode,
    applyPreset,
    priceMode,
    setPriceMode,
    currency,
    setCurrency,
    usePriceFactor,
    setUsePriceFactor,
    priceFactor,
    setPriceFactor,
    safePriceFactor,

    layout,
    setLayout,
    setLayoutField,
    selectedFieldKey,
    setSelectedFieldKey,
    selectedField,

    saveLayout,
    loadLayout,

    getDisplayName,
    getDisplayUom,
    getUnitPriceForPrint,
    getPLName,

    labels,
    totalBarcodes
  };
}

export type PriceListBarcodeVM = ReturnType<typeof usePriceListBarcodePrint>;
